import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import { setAssignedDevice } from "../redux/Clinic/clinic.slice";
import { clinicController } from "../AppController";
import { ASSIGNED_DEVICE_REFRESH_INTERVAL_MS } from "../constants/restDataRefresh";
import { useDocumentVisible } from "./useDocumentVisible";

/**
 * Periodically refetches assigned device (and connection status) from REST.
 * The timer is created/destroyed based on document visibility — when the app
 * goes to the background no timer exists at all (no wake-ups, no battery drain).
 * On foreground: immediate fetch + restart interval.
 */
export function useAssignedDevicePolling(): void {
  const dispatch = useAppDispatch();
  const visible = useDocumentVisible();
  const { patientId, clinicId, canPoll } = useAppSelector((state) => ({
    patientId: state.auth.user?.patient_id,
    clinicId: state.clinic.userClinic?.id,
    canPoll:
      !!state.clinic.clinicList?.length &&
      !!state.auth.user?.email &&
      !state.clinic.redirectToUserInfo,
  }));

  const refresh = useCallback(async () => {
    if (!patientId || patientId <= 0 || !clinicId) return;
    try {
      const { data } = await clinicController.fetchAssignedDevice({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      dispatch(setAssignedDevice(data || null));
    } catch {
      // Rely on next interval, focus, or reconnect.
    }
  }, [dispatch, patientId, clinicId]);

  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!canPoll || !patientId || patientId <= 0 || !clinicId || !visible) {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Foreground: immediate fetch then start interval.
    void refresh();
    intervalRef.current = window.setInterval(
      () => void refresh(),
      ASSIGNED_DEVICE_REFRESH_INTERVAL_MS
    );

    const onOnline = () => void refresh();
    window.addEventListener("online", onOnline);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      window.removeEventListener("online", onOnline);
    };
  }, [canPoll, patientId, clinicId, refresh, visible]);
}
